import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthenticateAzureMutation, useAuthenticateCiamMutation } from '@/entities/auth/api/authApi';
import { setAuthResponse, setLoading } from '@/entities/auth/model/slice';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useOidcAuth } from '@/features/login/model/use-oidc-auth';
import { type OidcProvider } from '@/shared/config/oidc-config';

export const useOidcCallback = (provider: OidcProvider) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [authenticateAzure] = useAuthenticateAzureMutation();
  const [authenticateCiam] = useAuthenticateCiamMutation();

  const { handleCallback } = useOidcAuth(provider);
  const [hasProcessed, setHasProcessed] = useState(false);

  const getAuthenticateFunction = useCallback(() => {
    switch (provider) {
      case 'azure':
        return authenticateAzure;
      case 'ciam':
        return authenticateCiam;
      default:
        throw new Error(`Unknown provider: ${provider}`);
    }
  }, [authenticateAzure, authenticateCiam, provider]);

  useEffect(() => {
    const processCallback = async () => {
      if (hasProcessed) {
        console.log('Already processed callback, skipping');
        return;
      }

      try {
        setHasProcessed(true);
        dispatch(setLoading(true));

        const oidcUser = await handleCallback();
        console.log(`${provider.toUpperCase()} OIDC User received:`, oidcUser);

        if (!oidcUser?.id_token) {
          throw new Error(`No ID token received from ${provider.toUpperCase()} OIDC`);
        }

        const authenticateFunction = getAuthenticateFunction();
        const result = await authenticateFunction(oidcUser.id_token).unwrap();

        if (result.success) {
          dispatch(setAuthResponse(result));
          console.log(`=== ${provider.toUpperCase()} authentication successful, navigating to home ===`);
          navigate('/', { replace: true });
        } else {
          throw new Error(`${provider.toUpperCase()} backend authentication failed`);
        }
      } catch (error) {
        console.error(`=== ${provider.toUpperCase()} OIDC authentication error ===`, error);
        navigate('/login', { replace: true });
      } finally {
        dispatch(setLoading(false));
        console.log(`=== ${provider.toUpperCase()} OIDC callback processing finished ===`);
      }
    };

    processCallback();
  }, [hasProcessed, handleCallback, provider, dispatch, navigate, getAuthenticateFunction]);

  return {
    isProcessing: !hasProcessed
  };
};
